import { useEffect, useState, useMemo } from 'react';
import { useClient } from '../../loco-sync';
import type { SG } from '@strength/loco-sync';
import { datadogLogs } from '@datadog/browser-logs';

function isEmpty(str: string | null | undefined): boolean {
  return str === null || str === undefined || str.trim() === '';
}

export function isValidSyncGroup(syncGroup: SG) {
  switch (syncGroup.type) {
    case 'TeamCoach':
    case 'TeamCoach_Timeline':
      return !isEmpty(syncGroup.teamId);
    case 'TeamCoach_BlockBuilder':
    case 'CrossTeamBlock_Readonly':
      return !isEmpty(syncGroup.blockId);
    case 'TeamCoach_BulkActuals':
      return (
        !isEmpty(syncGroup.teamId) &&
        !isEmpty(syncGroup.date) &&
        !isEmpty(syncGroup.timezone)
      );
    case 'TeamCoach_TabletAthlete':
      return (
        !isEmpty(syncGroup.teamId) &&
        !isEmpty(syncGroup.athleteId) &&
        // Fail closed on a missing anchor date: without it the server would
        // have to either throw or fall back to the unbounded history query
        // (SC-5518). Timezone is required like BulkActuals: the wire schema
        // tolerates its absence (server defaults to UTC), but the kiosk
        // always has a real one and the window's timestamp boundaries should
        // be device-local.
        !isEmpty(syncGroup.date) &&
        !isEmpty(syncGroup.timezone)
      );
    case 'TeamCoach_Calendar':
      return !isEmpty(syncGroup.teamId) && !isEmpty(syncGroup.date);
    case 'OrgCoach':
      return true;
  }
}

/**
 * Perform an intial load of data needed for a page to render. This only ever happens once
 * per unique sync group when establishing a new IDB.
 */
export function useBootstrap(syncGroups: SG[]) {
  const client = useClient();
  const [isLoading, setIsLoading] = useState(true);

  const validSyncGroups = useMemo(() => {
    return syncGroups.filter(isValidSyncGroup);
  }, [syncGroups]);

  useEffect(() => {
    if (validSyncGroups.length === 0) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    Promise.all(
      validSyncGroups.map((group) => client.ensureModelsLoaded(group)),
    )
      .catch((e) => {
        datadogLogs.logger.error('Error bootstrapping models', { error: e });
        console.error('Error loading models', e);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [client, validSyncGroups]);

  return { isLoading };
}
